import * as React from 'react';

type ID = string | number;
type Rows = Record<ID, boolean>;
export type StateType = {
  areAllRowsOpen: boolean;
  rows: Rows;
};

export type TwiddleOpenContextType = {
  rows: Rows;
  areAllRowsOpen: boolean;
  getOpenState: (id: string | number) => boolean;
  isRowRegistered: (id: string | number) => boolean;
  makeSetOpen: (id: string | number, openProp: boolean) => (open: boolean) => void;
  toggle: (id: string | number) => void;
  toggleAll: () => void;
  registerTwiddleRow: (id: string | number, openProp: boolean) => void;
};

export const openContextDefaultValue: TwiddleOpenContextType = {
  rows: {},
  areAllRowsOpen: false,
  getOpenState: () => false,
  isRowRegistered: () => false,
  makeSetOpen: () => () => {},
  toggle: () => {},
  toggleAll: () => {},
  registerTwiddleRow: () => {},
};

/**
 * Holds the state of all twiddle rows, including the "expand all" state.
 * Initialized at the `NewTable` level.
 */
export const TwiddleOpenContext = React.createContext<TwiddleOpenContextType>(openContextDefaultValue);

export type ActionType =
  | { type: 'REGISTER'; id: string | number; open: boolean }
  | { type: 'SET_OPEN'; id: string | number; open: boolean }
  | { type: 'TOGGLE'; id: string | number }
  | { type: 'TOGGLE_ALL' };

type ReducerType = React.Reducer<StateType, ActionType>;

const initialState: StateType = { areAllRowsOpen: false, rows: {} };

export const reducer: ReducerType = (state, action) => {
  switch (action.type) {
    case 'REGISTER': {
      if (state.rows[action.id] !== undefined) {
        return state;
      }
      return {
        ...state,
        rows: { ...state.rows, [action.id]: state.areAllRowsOpen || action.open },
      };
    }

    case 'SET_OPEN': {
      return {
        ...state,
        rows: { ...state.rows, [action.id]: action.open },
      };
    }

    case 'TOGGLE': {
      const rowState = state.rows[action.id];
      return {
        ...state,
        rows: { ...state.rows, [action.id]: !rowState },
      };
    }

    case 'TOGGLE_ALL': {
      return {
        areAllRowsOpen: !state.areAllRowsOpen,
        rows: Object.fromEntries(Object.keys(state.rows).map(row => [row, !state.areAllRowsOpen])),
      };
    }
    default:
      return state;
  }
};

export const TwiddleOpenContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [{ rows, areAllRowsOpen }, dispatch] = React.useReducer<ReducerType>(reducer, initialState);

  const makeSetOpen: TwiddleOpenContextType['makeSetOpen'] = React.useCallback(
    id => open => {
      dispatch({ type: 'SET_OPEN', id, open });
    },
    [],
  );
  const toggle: TwiddleOpenContextType['toggle'] = React.useCallback(id => {
    dispatch({ type: 'TOGGLE', id });
  }, []);
  const toggleAll: TwiddleOpenContextType['toggleAll'] = React.useCallback(() => {
    dispatch({ type: 'TOGGLE_ALL' });
  }, []);
  const registerTwiddleRow: TwiddleOpenContextType['registerTwiddleRow'] = React.useCallback((id, open) => {
    dispatch({ type: 'REGISTER', id, open });
  }, []);

  const contextValue: TwiddleOpenContextType = React.useMemo(
    () => ({
      rows,
      areAllRowsOpen,
      makeSetOpen,
      toggle,
      toggleAll,
      registerTwiddleRow,
      isRowRegistered: id => rows[id] !== undefined,
      getOpenState: id => (rows[id] !== undefined ? rows[id] : areAllRowsOpen),
    }),
    [areAllRowsOpen, makeSetOpen, registerTwiddleRow, rows, toggle, toggleAll],
  );

  return <TwiddleOpenContext.Provider value={contextValue}>{children}</TwiddleOpenContext.Provider>;
};

type TwiddleRowContextType = {
  id?: string | number;
  open?: boolean;
  level: number;
};

/**
 * Holds individual twiddle row state, including their own id. Initialized
 * at the `NewTable.Twiddle` level.
 */
export const TwiddleRowContext = React.createContext<TwiddleRowContextType>({
  id: 'uninitialized-context',
  open: undefined,
  level: 0,
});
